import { useEffect, useRef, useState } from 'react';

const SEARCH_PARAM = 'filter_feature_search';
const VIEW_PARAM = 'filter_feature_view';
const CATEGORY_PARAM = 'filter_feature_category';

/**
 * @param {string} key
 * @param {string[] | null} [validValues]
 */
const getUrlParam = (key, validValues = null) => {
	if (typeof window === 'undefined') return null;
	const value = new URL(window.location).searchParams.get(key);
	return !validValues || validValues.includes(value) ? value : null;
};

/**
 * @param {string} key
 * @param {string} value
 * @param {string | null} [deleteIfValue]
 */
const updateUrlParam = (key, value, deleteIfValue = null) => {
	const url = new URL(window.location);

	if (value === deleteIfValue) {
		url.searchParams.delete(key);
	} else {
		url.searchParams.set(key, value);
	}

	window.history.replaceState(null, '', url);
};

/**
 * @param {Object} options
 * @param {import('../types/feature').FeatureData | null} options.data
 * @param {import('../types/feature').FeatureView} options.defaultView
 */
export const useFilter = ({ data, defaultView }) => {
	const userInteracted = useRef(false);
	const [categoryFilterTerm, setCategoryFilterTermState] = useState(() => getUrlParam(CATEGORY_PARAM) || 'all');
	const [searchValue, setSearchValueState] = useState(() => getUrlParam(SEARCH_PARAM) || '');
	const [view, setViewState] = useState(
		/** @returns {import('../types/feature').FeatureView} */
		() => {
			const urlView = getUrlParam(VIEW_PARAM, ['grid', 'list']);
			return urlView === 'grid' || urlView === 'list' ? urlView : defaultView;
		},
	);

	// Set the selection to 'staff_pick' if it exists in the categories and no URL filter is active
	useEffect(() => {
		const hasUrlFilter = !!getUrlParam(CATEGORY_PARAM);

		if (data && !data.isFiltered && !hasUrlFilter && !userInteracted.current && categoryFilterTerm === 'all' && 'staff_pick' in data.categories) {
			setCategoryFilterTermState('staff_pick');
		}
	}, [categoryFilterTerm, data]);

	/** @param {string} slug */
	const setCategoryFilterTerm = (slug) => {
		userInteracted.current = true;
		setCategoryFilterTermState(slug);
		updateUrlParam(CATEGORY_PARAM, slug, 'all');
	};

	/** @param {string} value */
	const setSearchValue = (value) => {
		setSearchValueState(value);
		updateUrlParam(SEARCH_PARAM, value, '');
	};

	const clearSearchField = () => setSearchValue('');

	const handleViewChange = () => {
		setViewState((current) => {
			const next = current === 'grid' ? 'list' : 'grid';
			updateUrlParam(VIEW_PARAM, next);
			return next;
		});
	};

	return { categoryFilterTerm, setCategoryFilterTerm, searchValue, setSearchValue, clearSearchField, view, handleViewChange };
};
